import React, { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Tooltip,
  Button,
} from "@mui/material";
import {
  ArrowBackIos,
  ArrowForwardIos,
  FileDownload,
  Print,
} from "@mui/icons-material";
import { minWidth, styled } from "@mui/system";
import NoDataComponent from "@/components/NoData/NoDataComponent";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "rgb(234, 245, 238) !important",
    fontWeight: "bold",
    fontSize: "clamp(12px, 1.8vw, 14px)",
    height: "clamp(50px, 6vh, 60px)",
    fontFamily: "Lato",
    borderBottom: "2px solid #ddd",
  },
  "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
    backgroundColor: "rgb(234, 245, 238) !important", // Force override
    fontSize: "clamp(13px, 1.85vw, 15px)",
    height: "clamp(50px, 6vh, 60px)",
    fontFamily: "Lato",
    borderBottom: "2px solid #ddd",
  },
  "& .MuiDataGrid-cell": {
    fontSize: "clamp(10px, 1.6vw, 13px)",
    padding: "10px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontFamily: "Lato",
    alignContent: "center",
    outline: "none !important",
  },
  "& .MuiDataGrid-root": {
    border: "none",
  },
  "& .MuiDataGrid-virtualScroller": {
    overflowX: "auto !important",
  },
  "& .MuiDataGrid-columnHeader:first-of-type": {
    visibility: "hidden",
    pointerEvents: "none",
  },
  "& .MuiDataGrid-cell:first-of-type": {
    visibility: "hidden",
    pointerEvents: "none",
  },
}));

const CustomPagination = ({ count, page, setPage, pageSize, setPageSize }) => {
  const totalPages = Math.max(Math.ceil(count / pageSize), 1);
  const startRecord = count === 0 ? 0 : page * pageSize + 1;
  const endRecord = Math.min((page + 1) * pageSize, count);
  const maxPagesToShow = 5;

  const getDisplayedPages = () => {
    const pages = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0); // always show first

      const left = Math.max(page - 1, 1);
      const right = Math.min(page + 1, totalPages - 2);

      if (left > 1) pages.push("prevDots");

      for (let i = left; i <= right; i++) pages.push(i);

      if (right < totalPages - 2) pages.push("nextDots");

      pages.push(totalPages - 1); // always show last
    }

    return pages;
  };

  const handleDotsClick = (type) => {
    if (type === "prevDots") {
      setPage((prev) => Math.max(prev - 3, 0));
    } else if (type === "nextDots") {
      setPage((prev) => Math.min(prev + 3, totalPages - 1));
    }
  };

  return (
    <Box>
      {/* Top row: "Displaying..." aligned left */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start", // Align to start (left)
          alignItems: "center",
          width: "100%",
          px: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            fontSize: "clamp(10px, 1.6vw, 13px)",
            fontFamily: "Lato",
          }}
        >
          Displaying {startRecord} to {endRecord} of {count} records
        </Typography>
      </Box>

      {/* Pagination row: centered */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center", // Center the pagination
          alignItems: "center",
          mt: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>

          {getDisplayedPages().map((pg, idx) => {
            if (pg === "prevDots" || pg === "nextDots") {
              return (
                <Box
                  key={pg + idx}
                  onClick={() => handleDotsClick(pg)}
                  sx={{
                    width: 30,
                    height: 30,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#555",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  ...
                </Box>
              );
            }

            return (
              <Box
                key={pg}
                onClick={() => setPage(pg)}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  backgroundColor: page === pg ? "#2D6443" : "lightgray",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "clamp(12px, 1.8vw, 14px)",
                  cursor: "pointer",
                }}
              >
                {pg + 1}
              </Box>
            );
          })}

          <IconButton
            size="small"
            onClick={() => setPage(page + 1)}
            disabled={page + 1 >= totalPages}
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const CommonDataTable = ({
  rows,
  columns,
  search = true,
  pagination = true,
  pageSizeShow = true,
  checkbox,
  pagePerList,
  onSelectedRowsChange,
  disableRowSelectionOnClick,
  onRowSelectionModelChange,
  selectedRows,
  isRowSelectable,
  checkboxConditionFn = () => true,
  showEntriesBottom = false,
  exportOptions = {
    csv: false,
    excel: false,
    pdf: false,
    print: false,
  },
  exportOptionsName = {
    csv: "export",
    excel: "export",
    pdf: "export",
    print: "export",
  },
  loading = false,
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(pagePerList ?? 10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectionModel, setSelectionModel] = useState([]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  useEffect(() => {
    if (selectedRows) {
      const newSelection = selectedRows.map((row) => row.id);
      setSelectionModel(newSelection);
    }
  }, [selectedRows]);

  const flattenObject = (obj, parentKey = "", result = {}) => {
    for (let key in obj) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        flattenObject(value, fullKey, result);
      } else {
        result[fullKey] = value;
      }
    }
    return result;
  };

  const filteredRows = rows?.filter((row) => {
    const flattened = flattenObject(row);
    return Object.values(flattened).some((value) =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const rowsWithId = useMemo(
    () =>
      filteredRows?.map((row, index) => ({
        id: row.id ?? `${index}`,
        ...row,
      })),
    [filteredRows]
  );

  useEffect(() => {
    const totalRows = rowsWithId?.length;
    const totalPages = Math.ceil(totalRows / pageSize);

    if (page > 0 && page >= totalPages) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [rowsWithId?.length, page, pageSize]);

  const updatedColumns = [
    {
      field: "index",
      headerName: "S.No",
      minWidth: 100,
      renderCell: (params) => {
        // Find the row's index in the entire filtered dataset
        const rowIndex = rowsWithId?.findIndex((row) => row.id === params.id);
        return rowIndex >= 0 ? rowIndex + 1 : "";
      },
    },
    ...columns.map((col) => ({
      ...col,
      minWidth: col.minWidth || 180,
      renderCell: (params) => {
        const value = params.value;
        if (col.renderCell) {
          return col.renderCell(params);
        }
        return value !== null && value !== undefined && value !== ""
          ? value
          : "-";
      },
    })),
  ];

  const renderExportButtons = () => (
    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
      {exportOptions.csv && (
        <Tooltip title="Export as CSV">
          <Button
            sx={{
              height: "30px",
              borderRadius: "6px",
              background: "#2B5F40",
              color: "rgb(255, 255, 255)",
            }}
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => handleExport("csv")}
            size="small"
          >
            CSV
          </Button>
        </Tooltip>
      )}
      {exportOptions.excel && (
        <Tooltip title="Export as Excel">
          <Button
            sx={{
              height: "30px",
              borderRadius: "6px",
              background: "#2B5F40",
              color: "rgb(255, 255, 255)",
            }}
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => handleExport("excel")}
            size="small"
          >
            Excel
          </Button>
        </Tooltip>
      )}
      {exportOptions.pdf && (
        <Tooltip title="Export as PDF">
          <Button
            sx={{
              height: "30px",
              borderRadius: "6px",
              background: "#2B5F40",
              color: "rgb(255, 255, 255)",
            }}
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => handleExport("pdf")}
            size="small"
          >
            PDF
          </Button>
        </Tooltip>
      )}
      {exportOptions.print && (
        <Tooltip title="Print">
          <Button
            sx={{
              height: "30px",
              borderRadius: "6px",
              background: "#2B5F40",
              color: "rgb(255, 255, 255)",
            }}
            variant="outlined"
            startIcon={<Print />}
            onClick={() => handleExport("print")}
            size="small"
          >
            Print
          </Button>
        </Tooltip>
      )}
    </Box>
  );

  const renderPageSizeControl = () => (
    <>
      {pageSizeShow && (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="body2"
            sx={{
              mr: 1,
              fontWeight: "bold",
              fontSize: "clamp(10px, 1.6vw, 13px)",
              fontFamily: "Lato",
            }}
          >
            Show
          </Typography>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ height: 30, minWidth: 80 }}
          >
            {[5, 10, 20, 50, 100, { value: "all", label: "All" }].map(
              (size) => {
                const value = typeof size === "object" ? size.value : size;
                const label = typeof size === "object" ? size.label : size;
                return (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                );
              }
            )}
          </Select>
          <Typography
            variant="body2"
            sx={{
              ml: 1,
              fontWeight: "bold",
              fontSize: "clamp(10px, 1.6vw, 13px)",
              fontFamily: "Lato",
            }}
          >
            entries
          </Typography>
        </Box>
      )}
    </>
  );

  const handleSelectionChange = (newSelection) => {
    const validSelection = newSelection.filter((id) => {
      const row = rowsWithId?.find((r) => r.id === id);
      return checkboxConditionFn ? checkboxConditionFn(row) : true;
    });

    setSelectionModel(validSelection);

    const selectedRows = rowsWithId?.filter((row) =>
      validSelection.includes(row.id)
    );

    onSelectedRowsChange?.(selectedRows);
  };

  useEffect(() => {
    setSelectionModel([]);
    onSelectedRowsChange?.([]);
  }, [page, pageSize, searchQuery]);

  const handleExport = (type) => {
    const exportFileName =
      exportOptionsName?.[type] && exportOptionsName?.[type].trim() !== ""
        ? exportOptionsName?.[type]
        : "export";
    const dataToExport = rowsWithId?.map((row) => {
      const exportedRow = {};
      updatedColumns.forEach((col) => {
        if (col.field !== "index" && col.field !== "__check__") {
          exportedRow[col.headerName || col.field] = row[col.field];
        }
      });
      return exportedRow;
    });

    switch (type) {
      case "csv":
        exportToCSV(dataToExport, exportFileName);
        break;
      case "excel":
        exportToExcel(dataToExport, exportFileName);
        break;
      case "pdf":
        exportToPDF(dataToExport, exportFileName);
        break;
      case "print":
        window.print();
        break;
      default:
        console.warn(`Unknown export type: ${type}`);
    }
  };

  const exportToCSV = (data, fileName) => {
    if (!data || data.length === 0) return;

    const csvContent = [
      Object.keys(data[0]).join(","),
      ...data.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName + ".csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = (data, fileName) => {
    if (!data || data.length === 0) return;

    import("xlsx").then((XLSX) => {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, fileName + ".xlsx");
    });
  };

  const exportToPDF = (data, fileName) => {
    if (!data || data.length === 0) return;

    import("jspdf").then(({ default: jsPDF }) => {
      import("jspdf-autotable").then((autoTable) => {
        const doc = new jsPDF();
        autoTable.default(doc, {
          head: [Object.keys(data[0])],
          body: data.map((row) => Object.values(row)),
          styles: {
            cellPadding: 3,
            fontSize: 8,
            valign: "middle",
          },
          margin: { top: 20 },
        });
        doc.save(fileName + ".pdf");
      });
    });
  };

  // Calculate the actual page size to use (if "all" is selected, use all rows)
  const actualPageSize =
    pageSize === "all" ? rowsWithId?.length || 10 : pageSize;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {renderExportButtons()}
          {!showEntriesBottom && renderPageSizeControl()}
        </Box>

        {search && (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mb: 1, mt: 1 }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              sx={{
                width: "200px",
                backgroundColor: "white",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": { borderColor: "#ccc" },
                  "&:hover fieldset": { borderColor: "#888" },
                  "&.Mui-focused fieldset": { borderColor: "#4caf50" },
                },
              }}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Box>
        )}
      </Box>
      <StyledDataGrid
        loading={loading}
        rows={
          pageSize === "all"
            ? rowsWithId
            : rowsWithId?.slice(
                page * actualPageSize,
                (page + 1) * actualPageSize
              )
        }
        columns={updatedColumns}
        hideFooter
        checkboxSelection={checkbox}
        isRowSelectable={isRowSelectable}
        disableSelectionOnClick
        disableColumnMenu
        getRowHeight={() => "auto"}
        autoHeight
        sx={{
          minWidth: "100%",
          "& .hide-checkbox .MuiDataGrid-cellCheckbox": {
            visibility: "hidden",
            pointerEvents: "none",
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: "bold",
            outline: "none !important",
          },
        }}
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={selectionModel}
        onCellClick={(params, event) => {
          const isCheckboxCell = event.target.closest(
            '.MuiDataGrid-cellCheckbox input[type="checkbox"]'
          );
          if (!isCheckboxCell) {
            event.stopPropagation();
          }
        }}
        disableRowSelectionOnClick={disableRowSelectionOnClick ?? false}
        getRowClassName={(params) => {
          const isCheckboxVisible = checkboxConditionFn
            ? checkboxConditionFn(params.row)
            : true;
          return isCheckboxVisible ? "" : "hide-checkbox";
        }}
      />

      {pagination && pageSize !== "all" && (
        <CustomPagination
          count={filteredRows?.length || 0}
          page={page}
          setPage={setPage}
          pageSize={actualPageSize}
          setPageSize={setPageSize}
        />
      )}
      
      {showEntriesBottom && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          {renderPageSizeControl()}
        </Box>
      )}
    </Box>
  );
};

export default CommonDataTable;
